import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { ChangeDetectorRef, Component, ContentChild, forwardRef, inject, Input, TemplateRef } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormGroup } from "@angular/forms";

@Component({
	selector: "ic-order-input",
	styleUrls: ["order.component.scss"],
	templateUrl: "order.component.html",
	providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => OrderInputComponent), multi: true }],
	standalone: false,
})
export class OrderInputComponent implements ControlValueAccessor {
	private readonly cdRef = inject(ChangeDetectorRef);

	value: any[] = [];
	isDisabled: boolean;
	isFormVisible = false;

	private editIndex: number | undefined;

	@Input() items: any[];
	@Input() label: string;
	@Input() required = true;
	@Input() help: string;
	@Input() form: UntypedFormGroup;

	@ContentChild("itemTmpl", { static: false }) itemTemplate: TemplateRef<any>;
	@ContentChild("emptyTmpl", { static: false }) emptyTemplate: TemplateRef<any>;
	@ContentChild("addTmpl", { static: false }) addTemplate: TemplateRef<any>;
	@ContentChild("formTmpl", { static: false }) formTemplate: TemplateRef<any>;

	showForm(visible = true) {
		this.isFormVisible = visible;
		this.cdRef.markForCheck();
	}

	drop(event: CdkDragDrop<string[]>) {
		moveItemInArray(this.value, event.previousIndex, event.currentIndex);

		this.change();
	}

	remove(index: number) {
		this.value = [...this.value.slice(0, index), ...this.value.slice(index + 1)];

		this.change();
	}

	save() {
		if (this.editIndex !== undefined) {
			this.value[this.editIndex] = this.form.getRawValue();
		} else {
			this.value.push(this.form.getRawValue());
		}

		this.cancel();
		this.change();
	}

	edit(index: number) {
		// If there is no form template, early exit
		if (!this.formTemplate) {
			return;
		}

		this.editIndex = index;
		this.form.patchValue(this.value[index]);

		this.showForm();
	}

	cancel() {
		this.form.reset();
		this.editIndex = undefined;
		this.showForm(false);
	}

	// @Override
	writeValue(value: unknown[]) {
		this.value = value || [];
	}

	// @Override
	registerOnChange(fn: (v: unknown) => void) {
		this.onChange = fn;
	}

	// @Override
	registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	// @Override
	setDisabledState(isDisabled: boolean) {
		this.isDisabled = isDisabled;
	}

	private change() {
		this.onTouched();
		this.onChange(this.value);
	}

	private onChange = (_: unknown) => {}; // eslint-disable-line @typescript-eslint/no-empty-function
	private onTouched = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function
}
